/**
 * Settings → Notion (SPEC § Settings → Notion tab, §5.1 #40-#46).
 *
 * Saves **per action**, like the Scrapers tab and for the same reason: the database picker and the
 * mapping dropdowns need server-stored credentials to populate, so a single-draft form cannot work
 * — the key has to be committed before anything else can be asked of Notion.
 *
 * Three rules the server also enforces, mirrored here so the UI never lies:
 *  - the key is write-only (empty input, stored mask beside it, explicit Remove);
 *  - only columns whose type can hold a value are offered, and a column claimed by another row is
 *    disabled — #45 rejects the collision anyway;
 *  - picking a different database resets the mapping, so a non-empty one is confirmed first.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  notionFixedInputKinds,
  type NotionFixedInput,
  type NotionFixedValueDTO,
  type NotionPropertyDTO,
  type NotionSourceField,
  type NotionStateResponse,
} from '@lw/shared';
import { describeError } from '../../api/client';
import { ConfirmDialog, ErrorState, Spinner } from '../../components';
import {
  useNotionDatabasesQuery,
  useNotionQuery,
  useRefreshNotionSchema,
  useSetNotionDatabase,
  useSetNotionMapping,
  useSetNotionToken,
  useTestNotion,
} from './useNotion';

const MY_INTEGRATIONS_URL = 'https://www.notion.so/my-integrations';

/** `— don't save —`, the default of every mapping row. */
const UNMAPPED = '';

type Draft = {
  mapping: Partial<Record<NotionSourceField, string>>;
  fixedValues: { propId: string; value: NotionFixedInput }[];
  appBaseUrl: string;
};

function toDraft(state: NotionStateResponse): Draft {
  const mapping: Partial<Record<NotionSourceField, string>> = {};
  for (const [field, target] of Object.entries(state.mapping)) {
    if (target !== undefined) mapping[field as NotionSourceField] = target.propId;
  }
  return {
    mapping,
    fixedValues: state.fixedValues.map((fixed) => ({ propId: fixed.propId, value: fixed.value })),
    appBaseUrl: state.appBaseUrl ?? '',
  };
}

/** Every property id the draft has claimed — the disable rule for every other picker. */
function claimedIds(draft: Draft): Set<string> {
  const claimed = new Set<string>();
  for (const propId of Object.values(draft.mapping)) {
    if (typeof propId === 'string' && propId.length > 0) claimed.add(propId);
  }
  for (const fixed of draft.fixedValues) claimed.add(fixed.propId);
  return claimed;
}

/** `details: [{key, message}]` from a #45 `400`, flattened for inline rendering (§11.1). */
function fieldErrorsOf(error: unknown): Record<string, string> {
  const details = (error as { details?: unknown } | null)?.details;
  if (!Array.isArray(details)) return {};
  const errors: Record<string, string> = {};
  for (const entry of details) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { key, message } = entry as { key?: unknown; message?: unknown };
    if (typeof key === 'string' && typeof message === 'string') errors[key] = message;
  }
  return errors;
}

/* --------------------------------------------------------------- the section */

export function NotionSection() {
  const query = useNotionQuery();
  const state = query.data;

  if (query.isPending) return <Spinner size="lg" label="Loading Notion settings…" />;
  if (query.isError) {
    return (
      <ErrorState
        title="Couldn't load the Notion settings"
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }
  if (state === undefined) return null;

  return (
    <>
      {state.lastSkipped.length > 0 ? (
        <p className="settings-warn" role="status">
          The last save or schema read dropped {state.lastSkipped.join(', ')}. Use{' '}
          <strong>Refresh schema</strong> and re-map what is missing.
        </p>
      ) : null}
      <ConnectionSection state={state} />
      <DatabaseSection state={state} />
      {state.database === null ? null : <MappingSection state={state} />}
    </>
  );
}

/* ------------------------------------------------------------- 1 · connection */

function ConnectionSection({ state }: { state: NotionStateResponse }) {
  const save = useSetNotionToken();
  const test = useTestNotion();
  const [value, setValue] = useState('');

  return (
    <section className="settings-section">
      <h3 className="settings-title">Connection</h3>
      <div className="field">
        <label className="field__label" htmlFor="notion-token">
          Integration key
        </label>
        <input
          id="notion-token"
          className="input"
          type="password"
          autoComplete="off"
          placeholder="Enter a new value to replace"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
          }}
        />
        <span className="row muted settings-secret">
          {state.token.isSet ? `Stored: ${state.token.masked ?? '••••'}` : 'Not set'}
          {' · leave blank to keep it'}
          {state.token.isSet ? (
            <button
              type="button"
              className="btn-ghost btn-ghost--sm"
              onClick={() => {
                setValue('');
                save.mutate('');
              }}
              disabled={save.isPending}
            >
              Remove
            </button>
          ) : null}
        </span>
      </div>

      <div className="row settings-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            save.mutate(value, { onSuccess: () => { setValue(''); } });
          }}
          disabled={value.trim().length === 0 || save.isPending}
        >
          {save.isPending ? <Spinner size="sm" /> : null}
          Save key
        </button>
        <button
          type="button"
          className="table-action"
          onClick={() => {
            test.mutate();
          }}
          disabled={!state.token.isSet || test.isPending}
        >
          {test.isPending ? <Spinner size="sm" /> : null}
          <span className="table-action__label">Test connection</span>
        </button>
        {test.isSuccess ? (
          test.data.ok ? (
            <span className="settings-ok">{test.data.info}</span>
          ) : (
            <span className="settings-bad">{test.data.message}</span>
          )
        ) : null}
        {test.isError ? <span className="settings-bad">{describeError(test.error)}</span> : null}
      </div>

      {save.isError ? (
        <p className="form-error" role="alert">
          {describeError(save.error)}
        </p>
      ) : null}

      <p className="field__hint">
        Create an internal integration at{' '}
        <a href={MY_INTEGRATIONS_URL} target="_blank" rel="noreferrer noopener">
          notion.so/my-integrations
        </a>{' '}
        and paste its secret here. The database you want to save into must then be{' '}
        <strong>shared with that integration</strong> — open it in Notion, <code>···</code> →
        Connections → your integration.
      </p>
    </section>
  );
}

/* --------------------------------------------------------------- 2 · database */

function DatabaseSection({ state }: { state: NotionStateResponse }) {
  const setDatabase = useSetNotionDatabase();
  const refresh = useRefreshNotionSchema();
  const [wantList, setWantList] = useState(false);
  const databases = useNotionDatabasesQuery(wantList && state.token.isSet);
  const [url, setUrl] = useState('');
  const [pending, setPending] = useState<{ dataSourceId?: string; url?: string } | null>(null);

  const hasMapping =
    Object.keys(state.mapping).length > 0 || state.fixedValues.length > 0;

  const commit = (body: { dataSourceId?: string; url?: string }) => {
    if (hasMapping) setPending(body);
    else setDatabase.mutate(body);
  };

  /** `409 conflict` from #43 carries the data sources of a multi-source database (SPEC #43). */
  const choices = useMemo(() => {
    const details = (setDatabase.error as { details?: unknown } | null)?.details;
    if (typeof details !== 'object' || details === null) return [];
    const list = (details as { databases?: unknown }).databases;
    return Array.isArray(list) ? (list as NotionStateResponse['database'][]) : [];
  }, [setDatabase.error]);

  return (
    <section className="settings-section">
      <h3 className="settings-title">Database</h3>

      {state.database !== null ? (
        <div className="row settings-actions">
          <span>
            <strong>{state.database.databaseName ?? state.database.dataSourceName ?? 'Selected'}</strong>
            {state.schemaFetchedAtDisplay === null ? null : (
              <span className="muted"> · schema read {state.schemaFetchedAtDisplay}</span>
            )}
          </span>
          <button
            type="button"
            className="table-action"
            onClick={() => {
              refresh.mutate();
            }}
            disabled={refresh.isPending}
          >
            {refresh.isPending ? <Spinner size="sm" /> : null}
            <span className="table-action__label">Refresh schema</span>
          </button>
        </div>
      ) : (
        <p className="muted">No database is selected yet.</p>
      )}

      <div className="field">
        <label className="field__label" htmlFor="notion-database">
          Pick a database
        </label>
        {wantList && databases.isPending ? <Spinner size="sm" label="Asking Notion…" /> : null}
        {databases.data !== undefined ? (
          <select
            id="notion-database"
            className="select"
            value={state.database?.dataSourceId ?? UNMAPPED}
            disabled={setDatabase.isPending}
            onChange={(event) => {
              if (event.target.value.length > 0) commit({ dataSourceId: event.target.value });
            }}
          >
            <option value={UNMAPPED}>— select —</option>
            {databases.data.databases.map((entry) => (
              <option key={entry.dataSourceId} value={entry.dataSourceId}>
                {entry.databaseName ?? entry.dataSourceName ?? entry.dataSourceId}
                {entry.dataSourceName !== null && entry.dataSourceName !== entry.databaseName
                  ? ` — ${entry.dataSourceName}`
                  : ''}
              </option>
            ))}
          </select>
        ) : null}
        <div className="row settings-actions">
          <button
            type="button"
            className="table-action"
            onClick={() => {
              if (!wantList) setWantList(true);
              else void databases.refetch();
            }}
            disabled={!state.token.isSet || databases.isFetching}
          >
            {databases.isFetching ? <Spinner size="sm" /> : null}
            <span className="table-action__label">
              {databases.data === undefined ? 'Load databases' : 'Refresh list'}
            </span>
          </button>
        </div>
        {databases.isError ? (
          <span className="settings-bad">{describeError(databases.error)}</span>
        ) : (
          <span className="field__hint">
            The list holds every database shared with the integration.
          </span>
        )}
      </div>

      <div className="field">
        <label className="field__label" htmlFor="notion-url">
          …or paste a Notion database URL
        </label>
        <div className="row settings-actions">
          <input
            id="notion-url"
            className="input"
            type="text"
            autoComplete="off"
            placeholder="https://www.notion.so/…"
            value={url}
            onChange={(event) => {
              setUrl(event.target.value);
            }}
          />
          <button
            type="button"
            className="table-action"
            onClick={() => {
              commit({ url: url.trim() });
            }}
            disabled={url.trim().length === 0 || setDatabase.isPending}
          >
            <span className="table-action__label">Use this</span>
          </button>
        </div>
      </div>

      {choices.length > 0 ? (
        <div className="field">
          <span className="field__label">That database has several data sources — pick one</span>
          <div className="row settings-actions">
            {choices.map((choice) =>
              choice === null ? null : (
                <button
                  key={choice.dataSourceId}
                  type="button"
                  className="table-action"
                  onClick={() => {
                    commit({ dataSourceId: choice.dataSourceId });
                  }}
                >
                  <span className="table-action__label">
                    {choice.dataSourceName ?? choice.dataSourceId}
                  </span>
                </button>
              ),
            )}
          </div>
        </div>
      ) : null}

      {setDatabase.isError && choices.length === 0 ? (
        <p className="form-error" role="alert">
          {describeError(setDatabase.error)}
        </p>
      ) : null}
      {refresh.isError ? (
        <p className="form-error" role="alert">
          {describeError(refresh.error)}
        </p>
      ) : null}

      <ConfirmDialog
        open={pending !== null}
        title="Replace the target database?"
        message="The current field mapping and fixed values are cleared — the new database has different columns."
        confirmLabel="Replace"
        destructive
        busy={setDatabase.isPending}
        onCancel={() => {
          setPending(null);
        }}
        onConfirm={() => {
          if (pending !== null) setDatabase.mutate(pending, { onSettled: () => { setPending(null); } });
        }}
      />
    </section>
  );
}

/* ------------------------------------------------- 3+4+5 · mapping, fixed, base */

function MappingSection({ state }: { state: NotionStateResponse }) {
  const save = useSetNotionMapping();
  const [draft, setDraft] = useState<Draft>(() => toDraft(state));

  // Re-seed when the *stored* mapping actually changes (a save, a schema refresh that dropped
  // entries) — keyed on the content rather than the object, so a focus refetch that answers with
  // the same data does not discard what the user is halfway through typing.
  const serverKey = JSON.stringify([
    state.mapping,
    state.fixedValues,
    state.appBaseUrl,
    state.properties.map((property) => property.id),
  ]);
  useEffect(() => {
    setDraft(toDraft(state));
  }, [serverKey]);

  const byId = useMemo(
    () => new Map(state.properties.map((property) => [property.id, property])),
    [state.properties],
  );
  const claimed = claimedIds(draft);
  const errors = fieldErrorsOf(save.error);

  /** Only writable, still-unclaimed columns can carry a constant (SPEC § Fixed values). */
  const addableProperties = state.properties.filter(
    (property) => notionFixedInputKinds(property.type).length > 0 && !claimed.has(property.id),
  );

  const submit = () => {
    const mapping: Record<string, { propId: string; propName: string; propType: NotionPropertyDTO['type'] }> = {};
    for (const [field, propId] of Object.entries(draft.mapping)) {
      const property = propId === undefined ? undefined : byId.get(propId);
      if (property === undefined) continue;
      mapping[field] = { propId: property.id, propName: property.name, propType: property.type };
    }
    const fixedValues: NotionFixedValueDTO[] = [];
    for (const fixed of draft.fixedValues) {
      const property = byId.get(fixed.propId);
      if (property === undefined) continue;
      fixedValues.push({
        propId: property.id,
        propName: property.name,
        propType: property.type,
        value: fixed.value,
      });
    }
    save.mutate({ mapping, fixedValues, appBaseUrl: draft.appBaseUrl.trim() });
  };

  return (
    <>
      <section className="settings-section">
        <h3 className="settings-title">Field mapping</h3>
        <p className="field__hint">
          The page title is always written from the post's first line — it is not a row here. Only
          columns whose type can hold a value are offered.
        </p>
        <div className="settings-grid">
          {state.sourceFields.map((source) => {
            const selected = draft.mapping[source.field] ?? UNMAPPED;
            const options = state.properties.filter(
              (property) =>
                source.compatibleTypes.includes(property.type) &&
                (property.id === selected || !claimed.has(property.id)),
            );
            const id = `notion-map-${source.field}`;
            return (
              <div className="field" key={source.field}>
                <label className="field__label" htmlFor={id}>
                  {source.label}
                </label>
                <select
                  id={id}
                  className="select"
                  value={selected}
                  onChange={(event) => {
                    setDraft((current) => {
                      const mapping = { ...current.mapping };
                      if (event.target.value === UNMAPPED) delete mapping[source.field];
                      else mapping[source.field] = event.target.value;
                      return { ...current, mapping };
                    });
                  }}
                >
                  <option value={UNMAPPED}>— don't save —</option>
                  {options.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.name} ({property.type})
                    </option>
                  ))}
                </select>
                {errors[source.field] !== undefined ? (
                  <span className="settings-bad">{errors[source.field]}</span>
                ) : (
                  <span className="field__hint">{source.help}</span>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="settings-section">
        <h3 className="settings-title">Fixed values</h3>
        <p className="field__hint">These are written on every save, whatever the post is.</p>
        {draft.fixedValues.map((fixed, index) => {
          const property = byId.get(fixed.propId);
          if (property === undefined) return null;
          return (
            <div className="field" key={`${fixed.propId}-${String(index)}`}>
              <span className="field__label">
                {property.name} <span className="faint">({property.type})</span>
              </span>
              <div className="row settings-actions">
                <FixedValueEditor
                  property={property}
                  value={fixed.value}
                  onChange={(value) => {
                    setDraft((current) => ({
                      ...current,
                      fixedValues: current.fixedValues.map((entry, position) =>
                        position === index ? { ...entry, value } : entry,
                      ),
                    }));
                  }}
                />
                <button
                  type="button"
                  className="btn-ghost btn-ghost--sm"
                  onClick={() => {
                    setDraft((current) => ({
                      ...current,
                      fixedValues: current.fixedValues.filter((_, position) => position !== index),
                    }));
                  }}
                >
                  Remove
                </button>
              </div>
              {errors[`fixedValues.${String(index)}`] !== undefined ? (
                <span className="settings-bad">{errors[`fixedValues.${String(index)}`]}</span>
              ) : null}
            </div>
          );
        })}

        <div className="field">
          <label className="field__label" htmlFor="notion-add-fixed">
            + Add fixed value
          </label>
          <select
            id="notion-add-fixed"
            className="select"
            value={UNMAPPED}
            disabled={addableProperties.length === 0}
            onChange={(event) => {
              const property = byId.get(event.target.value);
              if (property === undefined) return;
              setDraft((current) => ({
                ...current,
                fixedValues: [
                  ...current.fixedValues,
                  { propId: property.id, value: defaultFixedInput(property) },
                ],
              }));
            }}
          >
            <option value={UNMAPPED}>— pick a column —</option>
            {addableProperties.map((property) => (
              <option key={property.id} value={property.id}>
                {property.name} ({property.type})
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="settings-section">
        <h3 className="settings-title">App base URL</h3>
        <div className="field">
          <label className="field__label" htmlFor="notion-base-url">
            Base URL (optional)
          </label>
          <input
            id="notion-base-url"
            className="input"
            type="url"
            autoComplete="off"
            placeholder="https://example.com"
            value={draft.appBaseUrl}
            onChange={(event) => {
              setDraft((current) => ({ ...current, appBaseUrl: event.target.value }));
            }}
          />
          {errors.appBaseUrl !== undefined ? (
            <span className="settings-bad">{errors.appBaseUrl}</span>
          ) : (
            <span className="field__hint">
              Only needed if you open this app on a different address than the one you want in
              Notion. Empty means the address this page is served from.
            </span>
          )}
        </div>

        <div className="row settings-actions">
          <button type="button" className="btn-primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? <Spinner size="sm" /> : null}
            Save mapping
          </button>
          {save.isSuccess ? <span className="settings-ok">Saved.</span> : null}
        </div>
        {save.isError && Object.keys(errors).length === 0 ? (
          <p className="form-error" role="alert">
            {describeError(save.error)}
          </p>
        ) : null}
      </section>
    </>
  );
}

/* ------------------------------------------------------------ fixed editors */

function defaultFixedInput(property: NotionPropertyDTO): NotionFixedInput {
  const [kind] = notionFixedInputKinds(property.type);
  switch (kind) {
    case 'texts':
      return { kind: 'texts', texts: [] };
    case 'number':
      return { kind: 'number', number: 0 };
    case 'checkbox':
      return { kind: 'checkbox', checkbox: true };
    case 'date':
      return { kind: 'saveDate' };
    default:
      return { kind: 'text', text: property.options?.[0]?.name ?? '' };
  }
}

/** One editor per property type — the SPEC § Fixed values table, rendered. */
function FixedValueEditor({
  property,
  value,
  onChange,
}: {
  property: NotionPropertyDTO;
  value: NotionFixedInput;
  onChange: (value: NotionFixedInput) => void;
}) {
  if (property.type === 'select' || property.type === 'status') {
    const options = property.options ?? [];
    return (
      <select
        className="select"
        value={value.kind === 'text' ? value.text : ''}
        onChange={(event) => {
          onChange({ kind: 'text', text: event.target.value });
        }}
      >
        <option value="">— none —</option>
        {options.map((option) => (
          <option key={option.id} value={option.name}>
            {option.name}
          </option>
        ))}
      </select>
    );
  }

  if (property.type === 'multi_select') {
    return <MultiSelectEditor property={property} value={value} onChange={onChange} />;
  }

  if (property.type === 'checkbox') {
    return (
      <label className="toggle">
        <input
          type="checkbox"
          checked={value.kind === 'checkbox' && value.checkbox}
          onChange={(event) => {
            onChange({ kind: 'checkbox', checkbox: event.target.checked });
          }}
        />
        <span>{value.kind === 'checkbox' && value.checkbox ? 'On' : 'Off'}</span>
      </label>
    );
  }

  if (property.type === 'number') {
    return (
      <input
        className="input"
        type="number"
        value={value.kind === 'number' ? String(value.number) : '0'}
        onChange={(event) => {
          const parsed = Number(event.target.value);
          onChange({ kind: 'number', number: Number.isFinite(parsed) ? parsed : 0 });
        }}
      />
    );
  }

  if (property.type === 'date') {
    return (
      <>
        <label className="toggle">
          <input
            type="checkbox"
            checked={value.kind === 'saveDate'}
            onChange={(event) => {
              onChange(event.target.checked ? { kind: 'saveDate' } : { kind: 'date', date: '' });
            }}
          />
          <span>Date of save</span>
        </label>
        {value.kind === 'saveDate' ? null : (
          <input
            className="input"
            type="date"
            value={value.kind === 'date' ? value.date : ''}
            onChange={(event) => {
              onChange({ kind: 'date', date: event.target.value });
            }}
          />
        )}
      </>
    );
  }

  return (
    <input
      className="input"
      type="text"
      value={value.kind === 'text' ? value.text : ''}
      onChange={(event) => {
        onChange({ kind: 'text', text: event.target.value });
      }}
    />
  );
}

/** Existing options as toggles, plus free text for a name Notion does not have yet. */
function MultiSelectEditor({
  property,
  value,
  onChange,
}: {
  property: NotionPropertyDTO;
  value: NotionFixedInput;
  onChange: (value: NotionFixedInput) => void;
}) {
  const [fresh, setFresh] = useState('');
  const selected = value.kind === 'texts' ? value.texts : [];
  const known = (property.options ?? []).map((option) => option.name);
  const all = [...known, ...selected.filter((name) => !known.includes(name))];

  const toggle = (name: string) => {
    onChange({
      kind: 'texts',
      texts: selected.includes(name)
        ? selected.filter((entry) => entry !== name)
        : [...selected, name],
    });
  };

  return (
    <div className="field">
      <div className="row settings-actions">
        {all.map((name) => (
          <label className="toggle" key={name}>
            <input type="checkbox" checked={selected.includes(name)} onChange={() => { toggle(name); }} />
            <span>{name}</span>
          </label>
        ))}
      </div>
      <div className="row settings-actions">
        <input
          className="input"
          type="text"
          placeholder="Add another value"
          value={fresh}
          onChange={(event) => {
            setFresh(event.target.value);
          }}
        />
        <button
          type="button"
          className="btn-ghost btn-ghost--sm"
          disabled={fresh.trim().length === 0}
          onClick={() => {
            const name = fresh.trim();
            if (name.length > 0 && !selected.includes(name)) {
              onChange({ kind: 'texts', texts: [...selected, name] });
            }
            setFresh('');
          }}
        >
          Add
        </button>
      </div>
    </div>
  );
}

export default NotionSection;
